// useSubstrateStatus — reads the substrate_weeks ledger to determine
// if the weekly substrate has data. Used by the doctrine system to
// distinguish "no edges found" (doctrine fallback) from "no fixtures
// priced at all" (infrastructure empty — legitimate but rare).

import { useCallback, useEffect, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface SubstrateWeekStatus {
  week_start: string;
  fixtures_discovered: number;
  fixtures_priced: number;
  fixtures_with_clv: number;
  status: "filling" | "ready" | "thin" | "empty" | "failed";
  completed_at: string | null;
}

export interface UseSubstrateStatusResult {
  currentWeek: SubstrateWeekStatus | null;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
}

export function useSubstrateStatus(): UseSubstrateStatusResult {
  const [week, setWeek] = useState<SubstrateWeekStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const client = getBrowserClient();
      const { data, error: err } = await (client as any)
        .from("substrate_weeks")
        .select("week_start,fixtures_discovered,fixtures_priced,fixtures_with_clv,status,completed_at")
        .order("week_start", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (err) throw new Error(err.message);
      setWeek(data as SubstrateWeekStatus | null);
    } catch (e: any) {
      setError(e.message ?? "Failed to load substrate status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const isEmpty = !loading && (!week || week.status === "empty" || week.fixtures_priced === 0);

  return { currentWeek: week, loading, error, isEmpty };
}
